import { Component, OnInit, OnDestroy } from '@angular/core';
import { User } from '../../core/models/user.model';
import { UserService } from '../../core/services/user.service';
import { LoadingService } from '../../core/services/loading.service';
import { SETTINGS } from '../../core/config/common.settings';
import { DefaultPageSize } from 'src/app/core/dto/defaultPage.size';
import { Pagination } from 'src/app/core/dto/pagination';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { AppUtils } from 'src/app/core/config/app.utils';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';

interface Users {
  createdAt: any;
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, OnDestroy {
  allUsers: User[] = [];
  searchName = '';
  profilePlaceholderPath: any = "../assets/img/profile_avatar.jpg";

  sortColumn = {
    compare: (a: Users, b: Users) => {
      return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
    },
  };

  statusFilters: { text: string; value: string }[] = [];

  filterStatusFn = (selected: any, item: User): boolean => {
    const vals = Array.isArray(selected) ? selected : selected != null ? [selected] : [];
    return !vals.length || (item?.active !== undefined &&
      vals.includes(item.active ? 'active' : 'inactive'));
  };

  activeFilter: boolean | null = null;
  lastFilterKey = '';
  private langSub: Subscription | undefined;

  pageSize = new DefaultPageSize();
  sortField: any;
  sortOrder: any;
  constructor(
    private userService: UserService,
    private loading: LoadingService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.buildFilters();
    this.langSub = this.translocoService.langChanges$.subscribe(() => this.buildFilters());
    this.loadAllUsers();
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  buildFilters(): void {
    const t = this.translocoService.translate.bind(this.translocoService);
    this.statusFilters = [
      { text: t('users.status.active'), value: 'active' },
      { text: t('users.status.inactive'), value: 'inactive' },
    ];
  }

  async loadAllUsers(): Promise<any> {
    this.loading.start();
    const pagination = new Pagination(this.pageSize);
    const payload: any = {
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      name: this.searchName,
      active: this.activeFilter !== null ? this.activeFilter : null
    };
    try {
      const users = await this.userService.getAllUsers(payload);
      this.allUsers = users.data;
      this.pageSize.length = users.metadata[0]?.total;
      this.loading.stop();
    } catch (e) {
      this.loading.stop();
    }
  }

  onTableParamsChange(params: NzTableQueryParams): void {
    this.pageSize.pageSize = params.pageSize;
    this.pageSize.filter = params.filter;

    const currentFilterKey = JSON.stringify(params.filter);
    if (currentFilterKey !== this.lastFilterKey) {
      this.pageSize.pageIndex = 1;
      this.lastFilterKey = currentFilterKey;
    } else {
      this.pageSize.pageIndex = params.pageIndex;
    }

    const currentSort = params.sort.find(item => item.value !== null);
    this.sortField = (currentSort && currentSort.key) || null;
    this.sortOrder =
      (currentSort && AppUtils.getSortOrder(currentSort.value)) || null;

    const statusVal = params.filter.find(f => f.key === 'status')?.value;
    const statusArr = Array.isArray(statusVal) ? statusVal : statusVal != null ? [statusVal] : [];
    this.activeFilter = statusArr.length ? statusArr[0] === 'active' : null;

    this.loadAllUsers();
  }

  reset(): void {
    this.searchName = '';
    this.loadAllUsers();
  }

  protected readonly SETTINGS = SETTINGS;
}
